<template>
  <div
    v-if="content"
    class="space-y-20"
  >
    <div class="rounded-b-4xl bg-cream pb-16">
      <dynamic-style :color="content?.kleur ?? '#3D4366'" />
      <header
        v-if="!loading"
        class="relative"
      >
        <div class="card-base absolute inset-0 z-0 flex items-end overflow-hidden bg-dynamic lg:inset-x-6 lg:-bottom-1/4 lg:bg-cover lg:bg-bottom lg:bg-no-repeat">
          <patterns-marble :color="content.secundaire_kleur ?? '#7270C2'" />
        </div>
        <div class="container relative space-y-4 py-6 lg:py-12">
          <div class="flex items-center justify-between">
            <BaseChipHeading
              title="Vacatures in"
              :chip="content.title"
              light
              single-line
              no-margin
            />
            <NuxtLink
              :to="{name: 'vacatures', query: showMoreQuery}"
              class="btn btn-md btn-primary hidden lg:inline-flex"
            >
              Bekijk vacatures
            </NuxtLink>
          </div>
          <DynamicIntro
            v-if="content.renderable_intro"
            :html="content.renderable_intro"
            class="prose max-w-full leading-loose text-white lg:columns-2"
            link-class="text-white"
          />
          <div
            v-else
            class="intro-holder leading-loose text-white lg:columns-2"
          >
            Bij Westerduin ben je aan het juiste adres voor vacatures in {{ content.title }}! Wij helpen jou aan de perfecte baan. Aan de hand van jouw wensen en kwaliteiten gaan we op zoek naar vacatures in
            {{ content.title }} die bij jou passen. Dit doen we in een kennismakingsgesprek waarin we je beter leren kennen. We hebben een ruime keus aan vacatures in
            {{ content.title }} binnen verschillende beroepsgroepen zoals <NuxtLink to="/vacatures-in-techniek">
              techniek
            </NuxtLink>, <NuxtLink to="/vacatures-in-groen">
              groen
            </NuxtLink>, <NuxtLink to="/vacatures-in-bouw">
              bouw
            </NuxtLink> en <NuxtLink to="/vacatures-in-logistiek">
              logistiek
            </NuxtLink>. Ben je op zoek naar een specifieke functie? Bekijk dan onze vacatures per functie. Genoeg mogelijkheden voor het vinden van jouw droombaan!
          </div>
        </div>
      </header>
      <section
        v-if="content?.active_image || vacancies.length"
        class="container relative z-10"
      >
        <div class="lg:card-base pt-6 lg:p-10">
          <div class="flex flex-col gap-6 md:flex-row lg:gap-x-20">
            <RecruiterTile
              v-if="recruiter"
              class="rounded-2xl border border-gray-lightest p-6 shadow-base lg:border-none lg:p-0 lg:shadow-none"
              :recruiter="recruiter"
              single-column
              no-card
            />
            <div
              v-if="content?.active_image"
              class="grow max-md:order-first"
            >
              <div class="card-base aspect-h-2 aspect-w-3 relative overflow-hidden rounded-2xl">
                <NuxtPicture
                  :src="content.active_image.url"
                  :img-attrs="{class: '!h-full w-full object-cover'}"
                  sizes="700px sm:1400px"
                />
                <div v-if="content.active_image?.title">
                  <div class="absolute bottom-6 right-6 rounded-lg bg-white px-6 py-2 text-xs text-violet">
                    {{ content.active_image.title }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    <section
      v-if="vacancies?.length"
      class="container"
    >
      <BaseChipTitle
        title="Openstaande"
        chip="vacatures"
        center
        single-line
        bg-color="bg-dynamic"
      />
      <div class="grid gap-6 md:grid-cols-3">
        <vacancy-tile
          v-for="vacancy in vacancies"
          :key="vacancy.id"
          :vacancy="vacancy"
        />
        <vacancy-show-more-card
          :title="'Meer vacatures in ' + content.title"
          :query="showMoreQuery"
        />
      </div>
    </section>
    <section v-else>
      <p class="text-center">
        Er zijn geen vacatures in {{ content.title }} gevonden
      </p>
    </section>
    <section
      v-if="content?.renderable_text"
      class="container"
    >
      <div class="card-base bg-cream px-4 py-6 lg:p-20">
        <DynamicIntro
          v-if="content.renderable_text"
          :html="content.renderable_text"
          class="prose"
        />
      </div>
    </section>
    <!--    <section class="px-8 pt-6 lg:hidden">-->
    <!--      <h2 class="pr-8 text-lg font-medium">-->
    <!--        Is dit niet iets voor jou maar perfect voor een vriend(in)? Deel het!-->
    <!--      </h2>-->
    <!--      <SocialLinksContainer class="mt-4" />-->
    <!--    </section>-->
    <section>
      <BaseChipTitle
        :title="professions ? 'Functies binnen' : 'In de buurt van'"
        :chip="content.title"
        center
        bg-color="bg-dynamic"
        multi-line-medium
      />
      <div class="container-overflow">
        <BaseCarousel
          :items="related"
          :config="swiperConfig"
          type="location"
          class="container-extend"
        >
          <template #item="{slug, title, active_image}">
            <NuxtLink :to="`/${slug}`">
              <div class="card-base block h-full">
                <NuxtPicture
                  v-if="active_image"
                  :img-attrs="{class: 'card-img aspect-1 w-full rounded-2xl lg:max-h-80'}"
                  :src="active_image.url"
                  :alt="title"
                  sizes="md:700px 1400px"
                />
                <div class="flex items-center justify-between px-6 py-8 lg:py-5">
                  <div class="text-violet lg:text-xl lg:leading-9">
                    {{ title }}
                  </div>
                  <IconsControlNext class="lg:scale-125" />
                </div>
              </div>
            </NuxtLink>
          </template>
        </BaseCarousel>
      </div>
    </section>
    <EmployerCarousel />
  </div>
</template>

<script setup lang="ts">
import {useRouteQuery} from '@vueuse/router';
import vacancyLandingPageQuery from '~/graphql/queries/vacancyLandingPage.gql';
import type {Vacancy, PageContent, Plaats, Functie, Recruiter} from '~/graphql/graphql';
import locationsInRadiusQuery from '~/graphql/queries/locationsInRadius.gql';

definePageMeta({
  header: {
    transparent: true,
    cream: true,
  },
  bg: 'bg-white',
});
const route = useRoute();
const routeSlug = computed(() => route.path.replace('/', ''));

const content = ref<PageContent>();
const isSector = ref(false);

const vacancies = ref<Vacancy[]>([]);
const professions = ref<Functie[]>([]);
const recruiter = ref<Recruiter>({} as Recruiter);

const routeFilter = useRouteQuery<string>('filters');
const {onResult, loading} = useQuery(vacancyLandingPageQuery, {
  slug: routeSlug,
  filters: routeFilter.value ? JSON.parse(routeFilter.value) : {sector: [], hours: []},
  order: route.query?.order ?? 'salary',
}, {
  fetchPolicy: 'no-cache',
});

onResult((landingPageResult) => {
  content.value = landingPageResult.data.vacancyLandingPage.page;
  vacancies.value = landingPageResult.data.vacancyLandingPage.vacancies;
  professions.value = landingPageResult.data.vacancyLandingPage.professions;
  recruiter.value = landingPageResult.data.vacancyLandingPage.recruiter;
  isSector.value = content.value?.type === 'sector';
});

const {result: locationResult} = useQuery(locationsInRadiusQuery, {
  slug: routeSlug,
});
const locations = computed<Plaats[]>(() => locationResult.value?.locationsInRadius ?? []);

const related = computed(() => {
  if (professions.value?.length) {
    return professions.value;
  } else {
    return locations.value;
  }
});

const showMoreQuery = computed(() => {
  return {filters: JSON.stringify((isSector.value ? {sector: [content.value?.title]} : {city: content.value?.title, radius: '20'}))};
});

const swiperConfig = {
  slidesPerView: 1.2,
  spaceBetween: 8,
  breakpoints: {
    786: {
      slidesPerView: 3.5,
      spaceBetween: 24,
    },
    1440: {
      slidesPerView: 4.5,
      spaceBetween: 24,
    },
  },
};
</script>

<style>
.intro-holder > a {
  @apply underline;
}
</style>
